/**
 * Race a promise against an AbortSignal. Resolves with the promise's value if it
 * settles first; rejects with AbortError if the signal fires first. Crucially,
 * the underlying promise is NOT canceled — only this caller's wait is canceled.
 *
 * Used by D1StatsProvider to give per-caller abort semantics over a shared
 * single-flight in-flight request.
 */
export function raceAbort<T>(promise: Promise<T>, signal: AbortSignal): Promise<T> {
  if (signal.aborted) {
    return Promise.reject(new DOMException('AbortError', 'AbortError'));
  }
  return new Promise<T>((resolve, reject) => {
    const onAbort = () => {
      signal.removeEventListener('abort', onAbort);
      reject(new DOMException('AbortError', 'AbortError'));
    };
    signal.addEventListener('abort', onAbort);
    promise.then(
      (value) => { signal.removeEventListener('abort', onAbort); resolve(value); },
      (err) => { signal.removeEventListener('abort', onAbort); reject(err); },
    );
  });
}
